import { useContext } from "react";

import { BTDropdown } from "@btnet/core-components";

import {
    BdsIconFinancialManagement,
    BdsIconGearSix,
    BdsIconSupport,
} from "@buildertrend/components";

import { WhenAppDefaultFlag } from "helpers/globalContext/AppDefaultInfoContext";
import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";

import { isBTAdminAuthProjectXEnabled } from "utilities/appDefaultFlags";
import { routes } from "utilities/routes";

import { BTIconCaretSmallDown } from "commonComponents/btWrappers/BTIcon";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { useStickyHeight } from "commonComponents/utilities/StickyContext";

import {
    HasSuperUserOrSupport,
    ShowAccountingWebhookTesting,
    ShowApplicationAndSearch,
    ShowBTAdminUsers,
    ShowBuilders,
    ShowBuilderSetup,
    ShowDevUtilitiesDiv,
    ShowElsa,
    ShowFeatureFlagsAndIntegration,
    ShowHangfire,
    ShowHawk,
    ShowLowesAccountStatus,
    ShowNotificationServiceTemplates,
    ShowOnlinePaymentReport,
    ShowPaymentsPrescreening,
    ShowRiskInsurance,
    ShowStuckPaymentUtility,
    ShowSubscriptionItemSetup,
    ShowSubscriptionPricingGuardrails,
    ShowSupportToolsAndBillingDiv,
} from "entity/admin/AdminMainPermissions";
import { hawkRouteConfig } from "entity/hawk/routes/routeConfig";
import { AdminOnlinePaymentReportTabs } from "entity/onlinePayment/AdminOnlinePaymentReports/AdminOnlinePaymentReports.types";

import "./BTAdminNavigation.less";

const BTADMIN_NAV_HEIGHT = 53;

const MainNavCaret: React.FunctionComponent = () => (
    <BTIconCaretSmallDown className="nav-bar-caret-down" />
);

interface IAdminMenuItemProps {
    link: string;
    text: string;
    key: string;
}

const AdminMenuItem: React.FunctionComponent<IAdminMenuItemProps> = (props) => (
    <BTMenu.Item testid={props.key + "-menu"} {...props}>
        <BTLink
            className="admin-main-link"
            to={props.link}
            id={props.key}
            isUnderline={false}
            useAutoSPARouting={true}
        >
            {props.text}
        </BTLink>
    </BTMenu.Item>
);

const AdminMenuItemWebforms: React.FunctionComponent<IAdminMenuItemProps> = (props) => (
    <BTMenu.Item testid={props.key + "-menu"} {...props}>
        <BTLink
            className="admin-main-link"
            to=""
            href={props.link}
            id={props.key}
            isUnderline={false}
            useAutoSPARouting={false}
        >
            {props.text}
        </BTLink>
    </BTMenu.Item>
);

const BTAdminNavigationInternal = () => {
    const envInfo = useContext(EnvironmentInfoContext);

    const iconStyles = { color: envInfo?.color };

    useStickyHeight(BTADMIN_NAV_HEIGHT, "top", 0);

    // support tools
    const showSupportToolsDiv = ShowSupportToolsAndBillingDiv();

    const showBuilders = ShowBuilders();
    const showBTAdminUsers = ShowBTAdminUsers();
    const showPaymentsPrescreening = ShowPaymentsPrescreening();
    const showHawk = ShowHawk();
    const showStuckPaymentUtility = ShowStuckPaymentUtility();
    const showBuilderSetup = ShowBuilderSetup();
    const showRiskInsurance = ShowRiskInsurance();
    const showLowesAccountStatus = ShowLowesAccountStatus();

    const Builders = (
        <AdminMenuItemWebforms
            link="/btadmin/adminBuilders.aspx"
            text="Builders - Legacy Page"
            key="support-builder"
        />
    );
    const BuildersNew = (
        <AdminMenuItem link={routes.admin.builders} text="Builders" key="support-builders-new" />
    );
    const BTAdminUsers = (
        <AdminMenuItem
            link={routes.admin.btAdminUsers}
            text="BTAdmin Users"
            key="support-btadmin-users"
        />
    );
    const PaymentsPrescreening = (
        <AdminMenuItem
            link={routes.onlinePayments.adminPrescreening}
            text="Payments Prescreening"
            key="support-payments"
        />
    );
    const Hawk = (
        <AdminMenuItem link={hawkRouteConfig.base.pathname} text="Hawk" key="support-hawk" />
    );
    const StuckPaymentUtility = (
        <AdminMenuItem
            link={routes.onlinePayments.stuckPaymentUtility}
            text="Stuck Payment Utility"
            key="support-stuck-payment"
        />
    );

    const RiskInsuranceUpdateUtility = (
        <AdminMenuItem
            link={routes.builderRiskInsurance.briUpdateUtility}
            text="Risk Insurance Update Utility"
            key="risk-insurance-update-utility"
        />
    );

    const BuilderSetup = (
        <AdminMenuItem
            link={routes.builderSetup.adminSearch}
            text="Builder Setup"
            key="support-builder-setup"
        />
    );

    const LowesAccountStatus = (
        <AdminMenuItem
            link={routes.admin.lowesAccountStatus}
            text="Lowes Account Status"
            key="support-lowes-account-status"
        />
    );

    const CustomerFeedback = (
        <AdminMenuItem
            link={routes.admin.customerFeedback}
            text="Customer Feedback"
            key="support-customer-feedback"
        />
    );

    // billing

    const showBillingReportsDiv = ShowSupportToolsAndBillingDiv();
    const showOnlinePaymentReport = ShowOnlinePaymentReport();
    const showSubscriptionItemSetup = ShowSubscriptionItemSetup();
    const showSubscriptionPricingGuardrails = ShowSubscriptionPricingGuardrails();

    const OnlinePaymentReport = (
        <AdminMenuItem
            link={routes.onlinePayments.getAdminMerchantPaymentListLink(
                AdminOnlinePaymentReportTabs.PaymentReport,
            )}
            text="Online Payment Report"
            key="billing-online-payment"
        />
    );

    const SubscriptionItemSetup = (
        <AdminMenuItem
            link={routes.subscriptionItemSetup.details}
            text="Subscription Item Setup"
            key="billing-subscription-setup"
        />
    );

    const SubscriptionPricingGuardrails = (
        <AdminMenuItem
            link={routes.admin.subscriptionPricingGuardrails.list}
            text="Subscription Pricing Guardrails"
            key="billing-pricing-guardrails"
        />
    );

    // dev utilities
    const showDevUtilitiesDiv = ShowDevUtilitiesDiv();
    const showHangfire = ShowHangfire();
    const showElsa = ShowElsa();
    const showAccountingWebhookTesting = ShowAccountingWebhookTesting();
    const showIntegrationPartners = ShowFeatureFlagsAndIntegration();
    const showFeatureFlags = ShowFeatureFlagsAndIntegration();
    const showSearchUtilities = ShowApplicationAndSearch();
    const showNotificationServiceTemplates = ShowNotificationServiceTemplates();
    const showApplicationSettings = ShowApplicationAndSearch();
    const showUsernameLookupTool = HasSuperUserOrSupport();
    const showSpamEmailRemovalTool = HasSuperUserOrSupport();

    const Hangfire = (
        <AdminMenuItemWebforms
            link="/apix/btadmin/hangfire"
            text="Hangfire"
            key="dev-util-hangfire"
        />
    );

    const ElsaDashboard = (
        <AdminMenuItemWebforms link="/elsa" text="Elsa Dashboard" key="dev-util-elsa" />
    );

    const AccountingWebhookTesting = (
        <AdminMenuItem
            link={routes.admin.accountingWebhookTesting}
            text="Accounting Webhook Testing"
            key="dev-util-accounting"
        />
    );

    const FeatureFlags = (
        <AdminMenuItem link={routes.featureFlags.list} text="Feature Flags" key="dev-util-flags" />
    );

    const IntegrationPartners = (
        <AdminMenuItem
            link={routes.partners.list}
            text="Integration Partners"
            key="dev-util-partner"
        />
    );

    const SearchUtilties = (
        <AdminMenuItem
            link={routes.search.btAdminUtility}
            text="Search Utilities"
            key="dev-util-search"
        />
    );

    const NotificationServiceTemplates = (
        <AdminMenuItem
            link={routes.admin.notificationServiceTemplates}
            text="Notification Service Templates"
            key="dev-util-notification-templates"
        />
    );

    const AppSettings = (
        <AdminMenuItem
            link={routes.admin.devUtilities.appSettings}
            text="Application Settings"
            key="dev-util-new-settings"
        />
    );

    const UsernameLookupTool = (
        <AdminMenuItem
            link={routes.admin.users.usernameLookup}
            text="Username Lookup Tool"
            key="dev-util-username"
        />
    );

    const SpamRemovalTool = (
        <AdminMenuItem
            link={routes.admin.spamEmailRemoval}
            text="Spam Email Removal Tool"
            key="dev-util-spam"
        />
    );

    function renderSupportTools(): JSX.Element | null {
        return (
            <>
                {showSupportToolsDiv && (
                    <div className="link">
                        <BTDropdown
                            testid="support-main-nav-button"
                            aria-label="Support Tools"
                            className="AdminDropDown"
                            overlayClassName="AdminMenuOverlay"
                            overlay={
                                <BTMenu testid="supportToolsMenu">
                                    {showBuilders && (
                                        <>
                                            {BuildersNew}
                                            <WhenAppDefaultFlag
                                                isEnabled="migrateAdminBuildersToReact"
                                                otherwise={Builders}
                                            />
                                        </>
                                    )}
                                    {showBTAdminUsers && BTAdminUsers}
                                    {showPaymentsPrescreening && PaymentsPrescreening}
                                    {showHawk && Hawk}
                                    {showStuckPaymentUtility && StuckPaymentUtility}
                                    {showRiskInsurance && RiskInsuranceUpdateUtility}
                                    {showBuilderSetup && BuilderSetup}
                                    {showLowesAccountStatus && LowesAccountStatus}
                                    {CustomerFeedback}
                                </BTMenu>
                            }
                        >
                            <div className="AdminMenuContainer">
                                <span>
                                    <BdsIconSupport size="lg" style={iconStyles} />
                                </span>
                                <span>
                                    Support Tools <MainNavCaret />
                                </span>
                            </div>
                        </BTDropdown>
                    </div>
                )}
            </>
        );
    }

    function renderBillingTools(): JSX.Element | null {
        return (
            <>
                {showBillingReportsDiv && (
                    <div className="link">
                        <BTDropdown
                            testid="billing-main-nav-button"
                            aria-label="Billing"
                            className="AdminDropDown"
                            overlayClassName="AdminMenuOverlay"
                            overlay={
                                <BTMenu testid="billingMenu">
                                    {showOnlinePaymentReport ? OnlinePaymentReport : null}
                                    {showSubscriptionItemSetup ? SubscriptionItemSetup : null}
                                    {showSubscriptionPricingGuardrails
                                        ? SubscriptionPricingGuardrails
                                        : null}
                                </BTMenu>
                            }
                        >
                            <div className="AdminMenuContainer">
                                <span>
                                    <BdsIconFinancialManagement size="lg" style={iconStyles} />
                                </span>
                                <span>
                                    Billing <MainNavCaret />
                                </span>
                            </div>
                        </BTDropdown>
                    </div>
                )}
            </>
        );
    }

    function renderDevUtilities(): JSX.Element | null {
        return (
            <>
                {showDevUtilitiesDiv && (
                    <div className="link">
                        <BTDropdown
                            testid="dev-main-nav-button"
                            aria-label="Dev Utilities"
                            className="AdminDropDown"
                            overlayClassName="AdminMenuOverlay"
                            overlay={
                                <BTMenu testid="devUtilitiesMenu">
                                    {showHangfire ? Hangfire : null}

                                    {showElsa ? ElsaDashboard : null}

                                    {showAccountingWebhookTesting ? AccountingWebhookTesting : null}

                                    {showFeatureFlags ? FeatureFlags : null}
                                    {showIntegrationPartners ? IntegrationPartners : null}
                                    {showSearchUtilities ? SearchUtilties : null}
                                    {showApplicationSettings ? AppSettings : null}
                                    {showNotificationServiceTemplates
                                        ? NotificationServiceTemplates
                                        : null}
                                    {showUsernameLookupTool ? UsernameLookupTool : null}
                                    {showSpamEmailRemovalTool ? SpamRemovalTool : null}
                                </BTMenu>
                            }
                        >
                            <div className="AdminMenuContainer">
                                <span>
                                    <BdsIconGearSix size="lg" style={iconStyles} />
                                </span>
                                <span>
                                    Dev Utilities <MainNavCaret />
                                </span>
                            </div>
                        </BTDropdown>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <div className="MainMenu">
                <div className="Title" style={iconStyles}>
                    BTAdmin - {envInfo?.name}
                </div>
                <div className="AdminMenuContainer">
                    <div className="link TextLink">
                        <BTLink
                            to={routes.admin.adminMain}
                            useAutoSPARouting={true}
                            className="admin-main-link"
                            id="main-nav-main"
                            isUnderline={false}
                        >
                            Main
                        </BTLink>
                    </div>
                    {renderSupportTools()}
                    {renderBillingTools()}
                    {renderDevUtilities()}

                    <div className="link TextLink">
                        <BTLink
                            to=""
                            href={
                                isBTAdminAuthProjectXEnabled()
                                    ? routes.admin.logoutLinkApix
                                    : routes.admin.logoutLink
                            }
                            useAutoSPARouting={false}
                            className="admin-main-link"
                            id="main-nav-main"
                            isUnderline={false}
                        >
                            Logout
                        </BTLink>
                    </div>
                </div>
            </div>
        </>
    );
};

export const BTAdminNavigation = () => {
    return (
        <>
            <BTAdminNavigationInternal />
        </>
    );
};
